import { useState } from "react";
import { Button, cn } from "@tellescope/ui";
import { Dialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";

export interface MobileListItem {
  id: string;
}

interface MobileListProps<T extends MobileListItem> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  getKey?: (item: T) => string;
  title?: string;
  maxItems?: number;
  emptyMessage?: string;
  className?: string;
}

export function MobileList<T extends MobileListItem>({
  items,
  renderItem,
  getKey,
  title,
  maxItems = 3,
  emptyMessage,
  className,
}: MobileListProps<T>) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasMore = items.length > maxItems;
  const visibleItems = hasMore ? items.slice(0, maxItems) : items;
  const remainingCount = items.length - maxItems;

  if (items.length === 0) {
    return (
      <div className={cn("text-center py-6 text-muted-foreground", className)}>
        {emptyMessage ?? t("No items to display")}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Visible items */}
      <div className="space-y-2">
        {visibleItems.map((item, index) => (
          <div key={getKey ? getKey(item) : item.id}>{renderItem(item, index)}</div>
        ))}
      </div>

      {/* View more button */}
      {hasMore && (
        <Button
          variant="ghost"
          className="w-full mt-3 text-muted-foreground hover:text-foreground"
          onClick={() => setIsModalOpen(true)}
        >
          {t("View {{amount}} more", { amount: remainingCount })}
        </Button>
      )}

      {/* Full list modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Dialog.Content className="max-w-md max-h-[85vh]">
          <Dialog.Header>
            <Dialog.Title>{title ?? t("All Items")}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className="gap-2 pr-2">
            {items.map((item, index) => (
              <div key={getKey ? getKey(item) : item.id}>{renderItem(item, index)}</div>
            ))}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
